import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { v4 as uuidv4 } from 'uuid';

import { JSM_INTEGRATION_KEY } from 'src/app/constants';
import { type Project } from 'src/models/projects';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { JsmService } from 'src/services/jsm.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';
import { UsersService } from 'src/services/users.service';

import { ConnectJsmInstanceModalComponent } from './connect-jsm-instance-modal/connect-jsm-instance-modal.component';
import { RelayRulesComponent } from './relay-rules/relay-rules.component';

interface JsmInstance {
  id: string;
  credentialId: string;
  name: string;
  url: string;
  isPrimary?: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RelayRulesComponent],
  selector: 'app-admin-jsm',
  templateUrl: './admin-jsm.component.html',
  styleUrls: ['./admin-jsm.component.scss'],
})
export class AdminJsmComponent implements OnInit {
  initialized = false;
  jsmEnabled: boolean;
  isLoading = false;
  connectedInstances: JsmInstance[] = [];
  projectId: string | null = null;
  projectName: string = '';

  constructor(
    public authService: AuthService,
    private usersService: UsersService,
    private route: ActivatedRoute,
    private msgService: MessageService,
    private jsmService: JsmService,
    private nangoService: NangoService,
    private apiService: ApiService,
    private modalCtrl: ModalController,
  ) {}

  async ngOnInit() {
    // Get projectId from route params (for project-specific settings pages)
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.load();
  }

  async load() {
    this.isLoading = true;

    const tenant = this.authService.tenant;

    // Check if JSM integration is enabled
    this.jsmEnabled = true; // TODO ENG-5249: What is the flag for this?

    // Only load JSM instances if we're at tenant level (no projectId)
    if (!this.projectId) {
      await this.loadJsmInstances();
    } else {
      const projects = (await this.usersService.getProjectsForMe()) as Project[];
      this.projectName = projects.find((project) => project.id === this.projectId)?.name ?? '';
    }

    this.initialized = true;
    this.isLoading = false;
  }

  async loadJsmInstances() {
    try {
      const credentials = await this.jsmService.getConnectedCredentials();

      this.connectedInstances = credentials.map((cred) => ({
        id: cred.jsmInstanceId,
        credentialId: cred.id, // Store credential ID for disconnect
        name: cred.jsmInstance?.name || 'Unknown Instance',
        url: cred.jsmInstance?.url || '',
      }));
    } catch (error) {
      this.msgService.show('Failed to load JSM instances');
    }
  }

  async connectJsmInstance(instanceName?: string, credentialId?: string) {
    // If instance name is provided (reconnect flow), skip prompt
    if (instanceName) {
      await this.performJsmOAuth({
        instanceName,
        credentialId,
      });

      return;
    }

    // Otherwise, show modal to prompt for Jira instance name
    const modal = await this.modalCtrl.create({
      component: ConnectJsmInstanceModalComponent,
      cssClass: 'connect-jsm-instance-modal',
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (data?.instanceName) {
      await this.performJsmOAuth({
        instanceName: data.instanceName,
        accountAccessToken: data.accountAccessToken,
      });
    }
  }

  async performJsmOAuth({
    instanceName,
    accountAccessToken,
    credentialId: existingCredentialId,
  }: {
    instanceName: string;
    accountAccessToken?: string;
    credentialId?: string;
  }) {
    const credentialId = `-${existingCredentialId ?? uuidv4()}`;

    try {
      this.isLoading = true;

      const { connectionId } = await this.nangoService.authorize(
        JSM_INTEGRATION_KEY, // Nango integration key
        {}, // No additional Nango config needed
        [credentialId], // Connection params for generating unique connectionId
      );

      await this.apiService.postPromise('/integrations/register', {
        integration: JSM_INTEGRATION_KEY,
        integrationType: JSM_INTEGRATION_KEY,
        isNango: true,
        connectionId,
        params: {
          instanceName,
          accountAccessToken,
        },
      });

      this.msgService.show('JSM instance connected successfully!');
      await this.loadJsmInstances();
    } catch (error) {
      this.msgService.show(`Failed to connect JSM instance: ${error.message || 'Unknown error'}`);
    } finally {
      this.isLoading = false;
    }
  }

  async disconnectJsmInstance(credentialId: string) {
    try {
      this.isLoading = true;

      await this.apiService.deletePromise(`/jsm/credentials/${credentialId}`);

      this.msgService.show('JSM instance disconnected successfully');
      await this.loadJsmInstances();
    } catch (error) {
      this.msgService.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async reconnectJsmInstance(instanceName: string, credentialId?: string) {
    // Just call the connect flow with the instance name - it will replace the old credential
    await this.connectJsmInstance(instanceName, credentialId);
  }

  async save() {
    if (!this.initialized) {
      return;
    }

    const updates: any = {
      // todo eng-5249 which JSM-specific settings are we adding
    };

    try {
      await this.authService.updateTenant(updates);

      this.load();
      this.msgService.show('✔︎ Saved');
    } catch (err) {
      this.msgService.show(`Something went wrong: ${err.message}`);
      this.initialized = true;
    }
  }
}
